import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ClipLoader } from 'react-spinners';

import { Area } from '../styles/_app.style';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { GapCover, BottomGapCover } from '../styles/search/search.style';
import { MessengerPageShell, MessengerPageWrapper } from '../styles/messenger/layout.style';

import { useConnections, Connection } from '../hooks/useConnections';
import { useMessages, Message } from '../hooks/useMessages';
import { useEvents } from '../hooks/useEvents';
import { useUser } from '../contextManager/user.context';
import { useOnline } from '../hooks/useOnline';
import { useScale } from './HeightScaler';

import { ConfirmationModal } from '../components/messenger/ConfirmationModal';
import { WarningModal } from '../components/messenger/WarningModal';
import { LessonCreationModal } from '../components/lessons/modals/LessonCreationModal';
import { TutorDetailView } from '../components/search/TutorDetailView';
import { ParentDetailView } from '../components/search/ParentDetailView';

import { validateMessage } from '../utils/messengerHelpers';
import { useMessengerRealtime } from '../hooks/useMessengerRealtime';
import { useMessageStatuses } from '../hooks/useMessageStatuses';
import { ConnectionList } from '../components/messenger/ConnectionList';
import { ChatPane } from '../components/messenger/ChatPane';
import { createPortal } from 'react-dom';
import { Dialog, Overlay } from '../styles/messenger/messenger.style';

const ADMIN_USER_ID = process.env.REACT_APP_ADMIN_USER_ID;

type UserProfile = Connection['parent'] | Connection['tutor'];

const isNetworkError = (err: any) =>
  (err?.isAxiosError && !err?.response) || err?.code === 'ERR_NETWORK';

const MessengerPage: React.FC = () => {
  const isOnline = useOnline();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { scale } = useScale();

  const { userDetails } = useUser();
  const { connections, isLoading, deleteConnections, updateConnection } = useConnections();
  const { events: myEvents } = useEvents();

  const [activeConnectionId, setActiveConnectionId] = useState<string | null>(null);
  const [viewingProfile, setViewingProfile] = useState<UserProfile | null>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [isLessonModalOpen, setLessonModalOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyModalMsg, setPolicyModalMsg] = useState('');
  const [policyBullets, setPolicyBullets] = useState<string[]>([]);

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const activeConnection = connections.find(conn => conn.id === activeConnectionId);
  const otherUser = activeConnection
    ? userDetails?.id === activeConnection.parentId
      ? activeConnection.tutor
      : activeConnection.parent
    : null;

  // Determine if the active connection is with the admin
  const isAdminConnection = !!(
    activeConnection &&
    ADMIN_USER_ID &&
    (activeConnection.parentId === ADMIN_USER_ID || activeConnection.tutorId === ADMIN_USER_ID)
  );

  const tutorForDetailView =
    viewingProfile?.role === 'tutor' ? viewingProfile : (userDetails as any);
  const parentForDetailView =
    viewingProfile?.role === 'parent' ? viewingProfile : (userDetails as any);

  const mobileViewState = activeConnectionId ? 'chat' : 'list';

  // Messages
  const {
    messages,
    isLoading: messagesIsLoading,
    sendMessage,
    markMessagesAsRead,
    retryMessage,
  } = useMessages(activeConnectionId);

  const messageStatuses = useMessageStatuses(messages, userDetails?.id);

  // Supabase + focus/visibility realtime hooks
  useMessengerRealtime({
    userId: userDetails?.id,
    activeConnectionId,
  });

  // Scroll to bottom when messages change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  // Deep link: ?open=connectionId
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    if (connections.length === 0) return;

    const exists = connections.some(c => c.id === openId);
    if (!exists) return;

    setActiveConnectionId(openId);
    setSearchParams({}, { replace: true });

    if ((location.state as any)?.newConnection) {
      navigate(location.pathname, { replace: true });
    }
  }, [connections, searchParams, setSearchParams, navigate, location.pathname, location.state]);

  // Auto-open: prioritize unread chat, then most recent
  useEffect(() => {
    if (activeConnectionId) return; // Already has an active chat
    if (connections.length === 0) return;
    if (searchParams.get('open')) return; // Let deep link handle it

    // Sort by most recent message first
    const sorted = [...connections].sort((a, b) => {
      const timeA = new Date(a.lastMessageAt || a.created_at).getTime();
      const timeB = new Date(b.lastMessageAt || b.created_at).getTime();
      return timeB - timeA;
    });

    // Prioritize first unread chat
    const unread = sorted.find(c => c.hasUnreadMessages);
    setActiveConnectionId(unread ? unread.id : sorted[0].id);
  }, [connections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark messages as read + optimistic update
  useEffect(() => {
    const markAndRefresh = async () => {
      if (!activeConnectionId || !userDetails?.id) return;
      if (messages.length === 0) return;

      const hasUnread = messages.some(
        msg => !msg.readAt && msg.senderId !== userDetails.id
      );

      if (!hasUnread) return;

      // optimistic: clear unread flag
      queryClient.setQueryData<Connection[] | undefined>(
        ['connections', userDetails.id],
        oldData => {
          if (!oldData) return [];
          return oldData.map(connection =>
            connection.id === activeConnectionId
              ? { ...connection, hasUnreadMessages: false }
              : connection
          );
        }
      );

      try {
        await markMessagesAsRead(activeConnectionId);
        await queryClient.invalidateQueries({
          queryKey: ['connections', userDetails.id],
        });
      } catch (error) {
        console.error('Failed to mark messages as read, reverting optimistic update:', error);
        queryClient.invalidateQueries({ queryKey: ['connections', userDetails.id] });
      }
    };

    markAndRefresh();
  }, [messages, activeConnectionId, userDetails?.id, markMessagesAsRead, queryClient]);

  // Promote "saved" -> "contacted" when opening
  useEffect(() => {
    if (!activeConnection) return;
    if (activeConnection.status === 'saved') {
      updateConnection({
        connectionId: activeConnection.id,
        newStatus: 'contacted',
      }).catch(console.error);
    }
  }, [activeConnection?.id, activeConnection?.status, updateConnection]);

  // ----- Handlers -----

  const handleStartReply = (message: Message) => setReplyingTo(message);
  const handleCancelReply = () => setReplyingTo(null);
  const handleMobileReturn = () => {
    setActiveConnectionId(null);
    setIsKeyboardOpen(false);
  }
  const handleToggleEdit = () => {
    setIsEditing(prev => !prev);
    setSelectedConnections(new Set());
  };

  const handleSelectionChange = (connectionId: string) => {
    setSelectedConnections(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(connectionId)) {
        newSelection.delete(connectionId);
      } else {
        newSelection.add(connectionId);
      }
      return newSelection;
    });
  };

  const handleRemove = () => {
    // Check if any selected connection is the admin connection
    const hasAdminSelected = ADMIN_USER_ID && connections.some(c => 
      selectedConnections.has(c.id) && 
      (c.parentId === ADMIN_USER_ID || c.tutorId === ADMIN_USER_ID)
    );

    if (hasAdminSelected) {
      setPolicyModalMsg('The Tute Support connection cannot be deleted.');
      setPolicyBullets(['Please deselect it and try again.']);
      setPolicyModalOpen(true);
      return;
    }

    setShowConfirmModal(true);
  };

  const executeDelete = async () => {
    if (selectedConnections.size === 0) return;

    try {
      const activeWasDeleted =
        activeConnectionId && selectedConnections.has(activeConnectionId);

      await deleteConnections(Array.from(selectedConnections));

      if (activeWasDeleted) {
        setActiveConnectionId(null);
        queryClient.invalidateQueries({ queryKey: ['messages', activeConnectionId] });
      }
    } catch (error) {
      console.error('Deletion failed:', error);
    } finally {
      setShowConfirmModal(false);
      handleToggleEdit();
    }
  };

  const handleSendMessage = async (message: string) => {
    setIsKeyboardOpen(false);
    if (!activeConnectionId || !activeConnection) return;

    const reasons = validateMessage(message, {
      meName: userDetails?.name,
      otherName: otherUser?.name,
      parentName: activeConnection.parent?.name,
      tutorName: activeConnection.tutor?.name,
    });

    if (reasons.length) {
      setPolicyModalMsg('Your message can\'t be sent:');
      setPolicyBullets(reasons);
      setPolicyModalOpen(true);
      return;
    }

    try {
      await sendMessage({
        connectionId: activeConnectionId,
        content: message,
        repliedToMessageId: replyingTo ? replyingTo.id : undefined,
        optimisticReplyTo: replyingTo
          ? {
              id: replyingTo.id,
              content: replyingTo.content,
              senderId: replyingTo.senderId,
            }
          : undefined,
      });

      if (userDetails?.id) {
        await queryClient.invalidateQueries({
          queryKey: ['connections', userDetails.id],
        });
      }
    } catch (err: any) {
      if (isNetworkError(err)) {
        console.log('Message failed due to network, user can retry via UI');
      } else {
        setPolicyModalMsg('Message failed to send.');
        setPolicyBullets(['Please try again in a moment.']);
      }
    } finally {
      setReplyingTo(null);
    }
  };

  const closeProfileModal = () => {
    setViewingProfile(null);
  }


  // ----- Render -----

  return (
    <>
    <Helmet><title>Messages - tute</title></Helmet>
    <Area
      css={{
        height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`,
        marginTop: `calc(${NAVIGATION_HEIGHT})`,
        overflow: 'hidden',
      }}
    >
        <MessengerPageShell>
          <MessengerPageWrapper mobileView={mobileViewState} 
                                className={isKeyboardOpen ? 'keyboard-open' : ''}
          >
          <ConnectionList
            connections={connections}
            isLoading={isLoading}
            currentUserId={userDetails?.id}
            currentUserRole={userDetails?.role}
            activeConnectionId={activeConnectionId}
            isEditing={isEditing}
            selectedConnections={selectedConnections}
            onToggleEdit={handleToggleEdit}
            onSelectionChange={handleSelectionChange}
            onSelectConnection={setActiveConnectionId}
            onRemoveClick={handleRemove}
          />

          <ChatPane
            onReturn={handleMobileReturn}
            activeConnectionId={activeConnectionId}
            activeConnection={activeConnection}
            currentUser={userDetails ?? null}
            messages={messages}
            messagesIsLoading={messagesIsLoading}
            otherUser={otherUser}
            replyingTo={replyingTo}
            onStartReply={handleStartReply}
            onCancelReply={handleCancelReply}
            onSendMessage={handleSendMessage}
            onRetry={retryMessage}
            onProfileClick={() => viewingProfile || setViewingProfile(otherUser)}
            onAddLessonClick={() => setLessonModalOpen(true)}
            messageStatuses={messageStatuses}
            scrollContainerRef={scrollContainerRef}
            isAdminConnection={isAdminConnection}
            onFocus={(e: React.FocusEvent) => {
              const target = e.target as HTMLElement;
              const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
              const isMobile = window.matchMedia('(pointer: coarse)').matches;
              if (isInput && isMobile) {
                setIsKeyboardOpen(true);
              }
           }}
            onBlur={() => setIsKeyboardOpen(false)}
          />

          <ConfirmationModal
            isOpen={showConfirmModal}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={executeDelete}
            title="Delete Connections"
            message={`Are you sure you want to delete the selected ${
              selectedConnections.size === 1 ? 'connection' : 'connections'
            }?`}
          />

          <WarningModal
            isOpen={policyModalOpen}
            onClose={() => setPolicyModalOpen(false)}
            onConfirm={() => setPolicyModalOpen(false)}
            title="Message blocked"
            message={policyModalMsg}
            bullets={policyBullets}
          />

          {userDetails && (
            <LessonCreationModal
              isOpen={isLessonModalOpen}
              onClose={() => setLessonModalOpen(false)}
              tutorName={otherUser?.name ?? null}
              tutorId={otherUser?.id ?? null}
              user={userDetails}
              tutorHourlyRate={(otherUser?.hourlyRate ?? 0) * 100}
              tutorTimezone={otherUser?.timezone}
              tutorAvailability={otherUser?.availability}
              existingEvents={myEvents}
              tutorOffersIntroCall={otherUser?.offersFreeIntroCall}
            />
          )}
          </MessengerPageWrapper>
        </MessengerPageShell>

        {viewingProfile && tutorForDetailView && parentForDetailView && createPortal(
        <Overlay onClick={closeProfileModal} style={{ zIndex: 9999 }}>
          <Dialog onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', height: '90vh', overflow: 'hidden', padding: 0, background: 'transparent', boxShadow: 'none', display: 'flex' }}>
            
            {viewingProfile.role === 'tutor' ? (
              <TutorDetailView
                tutor={tutorForDetailView}
                parent={parentForDetailView}
                onClose={closeProfileModal}
                showActions={false}
              />
            ) : (
              <ParentDetailView
                tutor={tutorForDetailView}
                parent={parentForDetailView}
                onClose={closeProfileModal}
                showActions={false}
              />
            )}
            
          </Dialog>
        </Overlay>,
        document.body
      )}
      
    </Area>
    </>
  );
};

export default MessengerPage;